import React, { useMemo, useState } from 'react';
import Badge from './ui/Badge';
import Button from './ui/Button';
import SafeguardingEventCard from './safeguarding/SafeguardingEventCard';
import { splitActiveHistorical, sortByRiskAndDate } from './safeguarding/safeguardingUtils';

const labelFromTitle = (title) =>
  title?.replace(/events?/i, '').trim().toLowerCase() || 'safeguarding';

export default function SafeguardingList({ title, events, permissions, onSensitiveEdit, onAddEvent }) {
  const isLoading = events == null;
  const visibleEvents = useMemo(
    () =>
      (events || []).filter((event) =>
        permissions?.canViewEvent ? permissions.canViewEvent(event) : true
      ),
    [events, permissions]
  );
  const { active, historical } = splitActiveHistorical(visibleEvents);
  const sortedActive = sortByRiskAndDate(active);
  const sortedHistorical = sortByRiskAndDate(historical);
  const [showHistorical, setShowHistorical] = useState(false);
  const labelBase = labelFromTitle(title);
  const labelDescriptor = labelBase === 'safeguarding' ? 'safeguarding' : `${labelBase} safeguarding`;
  const historicalId = `${labelBase.replace(/\\s+/g, '-')}-historical`;

  return (
    <section className="space-y-2" aria-label={`${title} safeguarding events`}>
      <div className="sp-section-header sp-section-header--surface flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>{title}</h3>
          <p className="text-sm sp-muted">Active items require attention before historical records.</p>
        </div>
        <Button size="sm" variant="secondary" onClick={onAddEvent} type="button" aria-label={`Add ${labelBase} event`}>
          Add Event
        </Button>
      </div>

      <div className="sp-subsection" aria-label={`${title} active items`}>
        <div className="sp-subsection-header">
          <div className="sp-subsection-title">Active items</div>
          <Badge variant={sortedActive.length ? 'review' : 'inactive'}>{sortedActive.length}</Badge>
        </div>
        {isLoading ? (
          <div className="text-sm sp-muted">Loading safeguarding events...</div>
        ) : sortedActive.length === 0 ? (
          <div className="text-sm sp-muted">No active {labelDescriptor} events.</div>
        ) : (
          <div className="space-y-2">
            {sortedActive.map((event) => (
              <SafeguardingEventCard
                key={event.id}
                event={event}
                onReview={onSensitiveEdit}
                onAssignOwner={onSensitiveEdit}
                onSetReviewDate={onSensitiveEdit}
              />
            ))}
          </div>
        )}
      </div>

      <div className="sp-subsection" aria-label={`${title} historical items`}>
        {isLoading ? null : sortedHistorical.length === 0 ? (
          <div className="text-sm sp-muted">No historical {labelDescriptor} events.</div>
        ) : (
          <>
            <button
              type="button"
              className="sp-action-link sp-action-link--muted"
              onClick={() => setShowHistorical((prev) => !prev)}
              aria-expanded={showHistorical}
              aria-controls={historicalId}
            >
              {showHistorical ? 'Hide' : 'Show'} closed / historical ({sortedHistorical.length})
            </button>
            {showHistorical && (
              <div id={historicalId} className="space-y-2">
                {sortedHistorical.map((event) => (
                  <SafeguardingEventCard
                    key={event.id}
                    event={event}
                    muted
                    onReview={onSensitiveEdit}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
